import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { readReportTab } from '@api/reports';
import { toast } from '@utilities';
import { downloadPresentation, validatePresentation } from '@utilities/powerpoint';

import { generateTabExportItems } from '../components/Tab/components/Visualizations/utilities/generate_tab_export_items';
import { buildReportPresentation } from './buildReportPresentation';

const NON_REPORT_TAB_IDS = ['ask-why', 'whats-next'];
const NON_EXPORTABLE_TAB_TAGS = new Set(['EXCESSIVE_DATASET']);

/**
 * Drives an "entire report" PPTX export, data-only: fetch each report tab (readReportTab), generate
 * its export items headlessly (generateTabExportItems — no React render, no DOM/screenshot), then
 * build one deck with a section per tab. Image-only visualizations are omitted (see the generator).
 *
 * @param  {Array}  askWhyPeopleGroups
 * @param  {Object} data         the report (needs tabs[], name, documentId, answers)
 * @param  {Object} downloadData current export accumulator (used for footer/shopperAttributes)
 * @param  {Array}  peopleGroupTypes
 * @param  {String} publicToken
 * @param  {Number} reportId
 * @return {Object} { isExporting, reportTabs, start }
 */
export const useEntireReportExport = ({
  askWhyPeopleGroups,
  data,
  downloadData,
  peopleGroupTypes,
  publicToken,
  reportId,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const exportControllerRef = useRef(null);

  useEffect(
    () => () => {
      exportControllerRef.current?.abort();
      exportControllerRef.current = null;
    },
    []
  );

  const reportTabs = useMemo(
    () =>
      (data?.tabs || []).filter((tab) => !NON_REPORT_TAB_IDS.includes(tab?.id) && !tab?.isDisabled),
    [data?.tabs]
  );

  const start = useCallback(async () => {
    if (isExporting || !reportTabs.length) return;
    exportControllerRef.current?.abort();
    const exportController = new AbortController();
    exportControllerRef.current = exportController;
    setIsExporting(true);

    try {
      const results = await Promise.all(
        reportTabs.map(async (tab) => {
          if (tab.tags?.some((tag) => NON_EXPORTABLE_TAB_TAGS.has(tag))) {
            return { isComplete: false, tab };
          }
          try {
            const tabData = (
              await readReportTab({
                publicToken,
                reportId,
                signal: exportController.signal,
                tabId: tab.id,
              })
            )?.data;
            const tabItems = generateTabExportItems({
              askWhyPeopleGroups,
              peopleGroupTypes,
              strict: true,
              tabData,
              tabId: tab.id,
              tabName: tab.label,
            });
            return { isComplete: tabItems.length > 0, tab, tabItems };
          } catch (error) {
            if (!exportController.signal.aborted) {
              console.error(`Report tab export failed: ${tab.label}`, error);
            }
            return { isComplete: false, tab };
          }
        })
      );

      if (exportController.signal.aborted) return;

      const sections = [];
      const omittedTabs = [];
      const { date_range: reportDateRange = [null, null] } = data?.answers || {};
      // Entire-report filenames describe the report, not whichever tab happened to be active.
      const fileName = data?.name || downloadData?.exportName || 'Report';
      const exportMetadata = {
        ...downloadData,
        reportDateRange,
        reportName: data?.name,
        reportType: data?.documentId,
        reportUrl: `${window.location.origin}/dashboard/report/${reportId}`,
      };
      let presentationMetadata;

      for (const { isComplete, tab, tabItems = [] } of results) {
        if (!isComplete) {
          omittedTabs.push(tab.label);
          continue;
        }
        try {
          // Canonical conversion and validation are part of the atomic tab boundary: a tab cannot
          // enter the combined deck merely because its legacy export-item types looked supported.
          // eslint-disable-next-line no-await-in-loop
          const tabModel = await buildReportPresentation({
            exportData: {
              ...exportMetadata,
              items: tabItems,
              tabsForExport: [{ tabId: tab.id, tabName: tab.label }],
            },
            fileName,
          });
          const validation = validatePresentation(tabModel);
          if (!validation.isValid) throw new TypeError(JSON.stringify(validation.errors));
          presentationMetadata ||= {
            fileName: tabModel.fileName,
            footer: tabModel.footer,
            title: tabModel.title,
          };
          sections.push(tabModel.sections[0]);
        } catch (error) {
          console.error(`Report tab canonical export failed: ${tab.label}`, error);
          omittedTabs.push(tab.label);
        }
      }

      if (!sections.length) {
        toast.error('Unable to export this report because no tabs could be included completely.');
        return;
      }

      await downloadPresentation({ ...presentationMetadata, sections });
      if (omittedTabs.length) {
        toast(`PowerPoint downloaded without incomplete tabs: ${omittedTabs.join(', ')}.`, {
          icon: '⚠️',
        });
      }
    } catch (error) {
      if (exportController.signal.aborted) return;
      console.error('Entire report export failed', error);
      toast.error('Unable to download PowerPoint. Please try again.');
    } finally {
      if (!exportController.signal.aborted) setIsExporting(false);
      if (exportControllerRef.current === exportController) exportControllerRef.current = null;
    }
  }, [
    askWhyPeopleGroups,
    data,
    downloadData,
    isExporting,
    peopleGroupTypes,
    publicToken,
    reportId,
    reportTabs,
  ]);

  return { isExporting, reportTabs, start };
};
